import React, { FC, useEffect, useMemo } from 'react';

import { Box, Stack } from '@mui/system';
import { Typography } from '@mui/material';

import {
  AdminLegendVariant,
  adminActions,
  getRequestedPharmacists,
  isLoadingSelector,
  requestedPharmacists,
} from '@pharmaplan/common';
import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import PharmacyCard from '../../SelfService/PharmacyCard';
import WithSkeleton from '../../common/WithSkeleton';
import { LocationText } from '../AvailableWorkshifts/AvailableWorkshifts';
import strings from '../../../localization';
import useGetItems from '../../../hooks/useGetItems';
import {
  renderScreen,
  setBack,
  setButtons,
  setFooterShow,
  setPagination,
} from '../../../actions/drawerActions';
import { ScreenTypes } from '../../../helpers/Constants';
import useOptimizedAvatar from '../../../hooks/useOptimizedAvatar';

import PharmacistList from './PharmacistList';
import styles from './styles';
import AdminHourlyRateFooter from '../../common/AdminHourlyRateFooter';
import useDetailsPagination from '../../../hooks/Admin/useDetailsPagination';
import ViewOtherRequestOffers from '../../SelfService/ViewOtherRequestOffers';
import { setDialog } from '../../../reducers/dialogReducer';
import AdminLongTermUserRates from '../../common/AdminHourlyRateFooter/AdminLongTermUserRates';

interface IRequestedPharmacistList {
  bookingIdList?: Array<string> | null;
  isAutoSyncRecord?: boolean;
  workshiftId: string;
}

const RequestedPharmacistList: FC<IRequestedPharmacistList> = ({
  workshiftId,
  bookingIdList,
  isAutoSyncRecord,
}) => {
  const successAction = adminActions.getRequestedPharmacists;
  const { id, paginationModel } = useDetailsPagination(bookingIdList);

  const dispatch = useAppDispatch();
  const { getImage } = useGetItems();
  const requestedPharmacistsList = useAppSelector(requestedPharmacists);

  const loading = useAppSelector((state) =>
    isLoadingSelector([successAction], state));
  const eventId = id ?? workshiftId;
  const isNotFromMap = !id;

  const pharmacyEvent = requestedPharmacistsList?.[0];
  const {
    pharmacyHourlyRate,
    pharmacyCity,
    logoId,
    pharmacyName,
    startDate,
    softwareName,
    pharmacyId,
    longTermShiftContract,
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    showOtherRequests,
    replacementType,
    campaignName,
    creditsLeft,
  } = pharmacyEvent ?? {};

  const isPromoRequest = !!creditsLeft;
  const discountTooltip = strings.formatString(strings.thisUserLinkedWithCampaignFormat, campaignName ?? '') as string;

  const SkeletonTypography = WithSkeleton({ successAction, variant: 'text' })(
    Typography,
  );

  const pharmacistIds = requestedPharmacistsList.map(
    (item) =>
item[idStore.id as keyof typeof item] as string,
  );

  const pIds = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      pharmacistIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestedPharmacistsList],
  );

  const {
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  } = longTermShiftContract ?? {};

  const { customPharmacyChip } = AdminLongTermUserRates({
    pharmacyWeekDayHourlyRate,
    pharmacyWeekendHourlyRate,
    weekDayHourlyRate: weekDayPaidHourlyRate,
    weekendHourlyRate: weekendPaidHourlyRate,
    contractStartDate: longTermShiftContract?.startDate,
    successAction,
  });

  const openOtherOffers = () => {
    dispatch(
      setDialog({
        heading: {
          title: strings.otherOffersForShift,
        },
        Component: (
          <ViewOtherRequestOffers
            professionalIds={pIds}
            workshiftId={eventId}
          />
        ),
        maxWidth: 'lg',
        showCloseButton: true,
      }),
    );
  };

  const goToRequestedList = () => {
    dispatch(
      renderScreen({
        screenNumber: 1,
        screenType: ScreenTypes.requestedList,
        date: startDate,
      }),
    );
  };

  const goToPharmacistRequestedList = () => {
    dispatch(
      renderScreen({
        screenNumber: 2,
        screenType: ScreenTypes.requestedList,
        eventId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const goToViewDetails = () => {
    dispatch(
      renderScreen({
        screenNumber: 3,
        screenType: ScreenTypes.availableWorkshift,
        eventId,
        type: AdminLegendVariant.requested,
        back: goToPharmacistRequestedList,
        pharmacyId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const count = loading ? 0 : requestedPharmacistsList.length;
  const showCount = `${strings.bookingRequests} (${count})`;

  const { getPharmacistImage } = useGetItems();

  const renderPharmacistList = () => {
    if (loading) {
      return (
        <PharmacistList
          event={pharmacyEvent}
          key={eventId}
          isAutoSyncRecord={isAutoSyncRecord}
        />
      );
    }

    return requestedPharmacistsList.map((item) =>
      (
        <PharmacistList
          image={getPharmacistImage(
          item[idStore.id as keyof typeof item] as string,
          )}
          event={item}
          key={item.workshiftId}
          isAutoSyncRecord={isAutoSyncRecord}
        />
      ));
  };

  useOptimizedAvatar(pIds);

  useEffect(() => {
    dispatch(getRequestedPharmacists({ id: eventId, isAutoSyncRecord }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    dispatch(setBack(goToRequestedList));

    if (longTermShiftContract?.longTermShiftContractId && showOtherRequests) {
      dispatch(setFooterShow(true));
      dispatch(
        setButtons({
          primaryButton: {
            label: null,
            onClick: null,
          },
          secondaryButton: {
            label: strings.viewOtherOffersForShift,
            onClick: openOtherOffers,
          },
          show: true,
        }),
      );
    }

    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  useEffect(() => {
    if (isNotFromMap) {
      dispatch(setPagination({ show: false }));
    } else {
      dispatch(setPagination(paginationModel));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotFromMap]);

  return (
    <Stack>
      <PharmacyCard
        longTermShiftContract={longTermShiftContract}
        isAutoSyncRecord={isAutoSyncRecord}
        type={AdminLegendVariant.requested}
        successAction={adminActions.getRequestedPharmacists}
        discountTooltip={discountTooltip}
        showDiscountIcon={isPromoRequest}
        CustomContent={(
          <LocationText
            containerStyle={styles.footerContainer}
            label={(
              <SkeletonTypography sx={styles.footer}>
                {pharmacyCity}
              </SkeletonTypography>
            )}
          />
        )}
        event={{
          ...pharmacyEvent,
          city: pharmacyName,
          logoImage: getImage(logoId),
          hourlyRate: pharmacyHourlyRate,
        }}
        CustomFooter={(
          <AdminHourlyRateFooter
            pharmacyHourlyRate={pharmacyHourlyRate}
            successAction={successAction}
            software={softwareName}
            replacementType={replacementType}
            customPharmacyChip={customPharmacyChip}
          />
        )}
        contentClick={goToViewDetails}
      />

      <Box sx={styles.titleContainer}>
        <Typography sx={styles.title}>{showCount}</Typography>
      </Box>

      {renderPharmacistList()}
    </Stack>
  );
};

export default RequestedPharmacistList;
